// Catalog.ID Client — Email Verification Landing Page
// ============================================================================
// Standalone page rendered at /verify#:token.
//
// Verification requires an explicit button click and goes out as a POST with
// the token in the body. Deliberately NOT fired on page load: email link
// scanners (Outlook SafeLinks, Gmail prefetch) load pages and would consume
// the verification before the user ever sees it; and a GET with the token in
// the URL would leave the token in server access logs.

import { createElement, createProgressBar } from '@catalog-shell';
import { api } from '../api/client';

export function renderVerifyLanding(container: HTMLElement, token: string): void {
    container.innerHTML = '';

    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card cid-verify' });
    wrapper.appendChild(card);
    container.appendChild(wrapper);

    const messageEl = createElement('p', { className: 'cid-verify-message' });

    if (!token) {
        messageEl.textContent = 'This verification link is incomplete. Open the link from your email again, or request a new verification from the dashboard.';
        messageEl.classList.add('cid-verify-error');
        card.appendChild(messageEl);

        const link = createElement('a', { className: 'cid-auth-link', textContent: 'Go to login' });
        link.setAttribute('href', '/');
        card.appendChild(link);
        return;
    }

    const heading = createElement('h2', { textContent: 'Verify your email address' });
    card.appendChild(heading);

    const intro = createElement('p', {
        className: 'cid-verify-message',
        textContent: 'Click the button below to confirm this email address for your Catalog.ID account.',
    });
    card.appendChild(intro);

    const button = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Verify email address',
    }) as HTMLButtonElement;
    card.appendChild(button);

    const progress = createProgressBar({ indeterminate: true, label: 'Verifying...' });
    progress.element.style.display = 'none';
    card.appendChild(progress.element);
    card.appendChild(messageEl);

    button.addEventListener('click', () => {
        button.disabled = true;
        button.style.display = 'none';
        intro.style.display = 'none';
        progress.element.style.display = '';

        void (async () => {
            try {
                // The response kind distinguishes a claim validation from a
                // password-reset confirmation (password-reset plan §5.4.4).
                const result = await api.post<{ success: boolean; kind?: string }>('/api/verify', { token });

                progress.setIndeterminate(false);
                progress.setValue(100);
                progress.setVariant('success');
                progress.setLabel('Verified');

                if (result.kind === 'password_reset') {
                    heading.textContent = 'Password reset confirmed';
                    messageEl.textContent = 'Return to the window where you started the reset — it will '
                        + 'continue automatically and let you set your new password. You can close this page.';
                    messageEl.classList.add('cid-verify-success');
                    return;
                }

                messageEl.textContent = 'Your email address has been verified.';
                messageEl.classList.add('cid-verify-success');

                // No link: this tab was opened only to click the button, and
                // what asked for the verification — the registration wizard,
                // which notices it by itself, or Settings — is in the tab the
                // person came from.
                card.appendChild(createElement('p', {
                    className: 'cid-verify-message',
                    textContent: 'You can close this tab and go back to where you started.',
                }));
            } catch (err) {
                progress.setIndeterminate(false);
                progress.setVariant('error');
                progress.setLabel('Failed');

                messageEl.textContent = err instanceof Error ? err.message : 'This verification link is invalid or has expired.';
                messageEl.classList.add('cid-verify-error');

                card.appendChild(createElement('p', {
                    className: 'cid-pane-note',
                    textContent: 'You can request a new verification from the dashboard.',
                }));

                const link = createElement('a', { className: 'cid-auth-link', textContent: 'Go to login' });
                link.setAttribute('href', '/');
                card.appendChild(link);
            }
        })();
    });
}
